import type { ReactNode } from "react";

/**
 * A column header centered over a narrow, left-pinned body cluster that sits inside a
 * WIDER track — e.g. a small icon cluster pinned to the start of a `1fr` column.
 *
 * It renders an INVISIBLE replica of the body's footprint (passed as `footprint`) to
 * size itself, then centers `children` (the header label) over it — no magic width to
 * tune. Both the replica and the label share one grid cell, which sizes to
 * `max(footprint, label)`: a label NARROWER than the footprint centers within it (over
 * the cluster's midpoint); a label WIDER than the footprint grows the cell to the label
 * (the footprint centers within the label, so the header is left-pinned and visually
 * balanced above the cluster) — never overflowing as long as the track has room.
 *
 * USE WHEN: the body content is narrower than its column and pinned to the column's
 * start edge, and the header must sit directly over that content (not over the whole
 * column). First consumer: the upload-column header ("Supporting Documents") over the
 * upload-icon cluster in ExpandableSectionLayout.
 *
 * DO NOT USE WHEN: the body fills its track (a normal input / field cell). There the
 * header should just left-align at the track start — this primitive adds nothing.
 *
 * CONTRACT: `footprint` must be a non-interactive node whose RENDERED WIDTH equals the
 * body cluster's. The reliable way to guarantee that is to build both the real body
 * and the footprint from the SAME shared constants (labels + spacing), so they can't
 * drift. The footprint is `aria-hidden` + `invisible`: it establishes width only and
 * is never read or shown. The component is itself a grid item and left-pins via
 * `justify-self-start` to match a start-pinned body cluster; both the replica and the
 * label occupy the same single grid cell so they stack exactly.
 */
export function HeaderOverContent({
  footprint,
  children,
  className,
}: {
  footprint: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={`grid justify-self-start text-center${className ? ` ${className}` : ""}`}>
      <span aria-hidden="true" className="invisible col-start-1 row-start-1">
        {footprint}
      </span>
      <span className="col-start-1 row-start-1">{children}</span>
    </span>
  );
}
